/**
 * RBAC-3.2 · FR-1, FR-3, FR-4, FR-4b, FR-5. Create, rename and delete a role the account owns.
 *
 * ⚠️ THE DATABASE IS THE ENFORCEMENT; THIS IS THE WORDING.
 *
 * `20261101b3_rbac_3_2_role_lifecycle.sql` decides everything:
 *   - `create_account_role(name, description)`: a tenant role in the caller's account;
 *   - `rename_account_role(role, name, description)`: refused (55006) while a pipeline names it;
 *   - `role_delete_check(role)`: what holds it, read off `role_reference_registry`;
 *   - `delete_account_role(role)`: refused (55006) while anything does; the grants go with it.
 * The name rule is RBAC-1.1's trigger (23505, its own sentence). Joules roles are 42501 "read only".
 *
 * ⚠️ HOLDERS ARE NOT ONLY PEOPLE. A role with nobody on it can still be held by an approval policy,
 * a dashboard, an access setting or a pipeline, so the delete dialog asks the database rather than
 * reading the holder count on screen. FR-4b: a refusal names what holds it and links there.
 */

export const CREATE_ACCOUNT_ROLE_RPC = 'create_account_role';
export const RENAME_ACCOUNT_ROLE_RPC = 'rename_account_role';
export const ROLE_DELETE_CHECK_RPC = 'role_delete_check';
export const DELETE_ACCOUNT_ROLE_RPC = 'delete_account_role';

/** The PostgREST error fields this module reads. */
export interface RpcErrorLike {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

export interface RoleHolder {
  noun: string;
  nounPlural: string;
  /** Where to go to move it off this role; null where no screen exists yet. */
  href: string | null;
  count: number;
}

export interface RoleDeleteCheck {
  roleId: number;
  name: string;
  deletable: boolean;
  holders: RoleHolder[];
  /** "3 people and 1 approval policy", from the server, or null when nothing holds it. */
  summary: string | null;
}

export interface RoleWriteResult {
  roleId: number;
  name: string;
}

const asNumber = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function parseHolders(raw: unknown): RoleHolder[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((h) => {
    if (!h || typeof h !== 'object') return [];
    const r = h as Record<string, unknown>;
    const count = asNumber(r.count);
    if (typeof r.noun !== 'string' || count === null) return [];
    return [{
      noun: r.noun,
      nounPlural: typeof r.noun_plural === 'string' ? r.noun_plural : r.noun,
      href: typeof r.href === 'string' && r.href.startsWith('/') ? r.href : null,
      count,
    }];
  });
}

/**
 * Server shape → view model. A shape without `deletable` is refused (null), so the dialog can never
 * offer a delete on a check it did not understand.
 */
export function parseDeleteCheck(raw: unknown): RoleDeleteCheck | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const roleId = asNumber(r.role_id);
  if (roleId === null || typeof r.name !== 'string' || typeof r.deletable !== 'boolean') return null;
  const holders = parseHolders(r.holders);
  return {
    roleId,
    name: r.name,
    // Belt and braces: anything listed as holding the role means not deletable, whatever the flag says.
    deletable: r.deletable && holders.length === 0,
    holders,
    summary: typeof r.summary === 'string' ? r.summary : null,
  };
}

export function parseRoleWriteResult(raw: unknown): RoleWriteResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const roleId = asNumber(r.role_id);
  return roleId === null || typeof r.name !== 'string' ? null : { roleId, name: r.name };
}

/** "3 people", "1 approval policy". */
export function holderPhrase(h: Pick<RoleHolder, 'noun' | 'nounPlural' | 'count'>): string {
  return `${h.count} ${h.count === 1 ? h.noun : h.nounPlural}`;
}

/** The client's only name rule. Uniqueness and Joules names are the database's (RBAC-1.1). */
export function roleNameProblem(name: string): string | null {
  return name.trim() === '' ? 'A role needs a name.' : null;
}

/** A refusal's holders, read from the 55006 DETAIL the server attaches. */
export function holdersFromError(err: RpcErrorLike | null | undefined): RoleHolder[] {
  if (err?.code !== '55006' || !err.details) return [];
  try {
    return parseHolders(JSON.parse(err.details));
  } catch {
    return [];
  }
}

export function roleLifecycleErrorMessage(err: RpcErrorLike | null | undefined, fallback: string): string {
  if (err?.code === 'P0002') return 'This role no longer exists. Reload to see the current list.';
  const message = err?.message?.trim();
  if (!message) return fallback;
  const hint = err?.hint?.trim();
  return hint ? `${message} ${hint}` : message;
}

export interface DeleteDialogCopy {
  title: string;
  lead: string;
  confirmLabel: string | null;
}

export function deleteDialogCopy(check: RoleDeleteCheck, permissionCount: number): DeleteDialogCopy {
  if (!check.deletable) {
    return {
      title: `"${check.name}" can't be deleted yet`,
      lead: `It is in use by ${check.summary ?? check.holders.map(holderPhrase).join(', ')}. Move them off this role first, then delete it.`,
      confirmLabel: null,
    };
  }
  const grants =
    permissionCount === 0
      ? 'It carries no permissions.'
      : `Its ${permissionCount === 1 ? '1 permission goes' : `${permissionCount} permissions go`} with it.`;
  return {
    title: `Delete "${check.name}"?`,
    lead: `Nobody and nothing uses this role. ${grants} This cannot be undone.`,
    confirmLabel: 'Delete role',
  };
}
